import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import './Chart.css';

const RecencyChart = ({ countyData, indianaAverages, touchpointTypes, selectedYear }) => {
  const svgRef = useRef();
  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!containerRef.current) return;

    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight
        });
      }
    };

    updateDimensions();
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const dataSource = countyData || indianaAverages;
    if (!dataSource || !selectedYear || dimensions.width === 0) {
      return;
    }

    const fixedOrder = ['ED visit', 'EMS interaction', 'Jail booking', 'Release from prison', 'Rx dispensation', 'Rx dispensation for Buprenorphine'];
    const dataForChart = touchpointTypes.map(type => {
      const yearData = dataSource.touchpoints[type]?.[selectedYear];
      const stateYearData = countyData && indianaAverages?.touchpoints[type]?.[selectedYear];
      return {
        type,
        value: yearData?.['Average Days Since Most Recent Touchpoint Utilization'] || 0,
        count: yearData?.['Decedent Touchpoint Individual Count'] || 0,
        stateAvg: stateYearData?.['Average Days Since Most Recent Touchpoint Utilization'] || null
      };
    }).sort((a, b) => fixedOrder.indexOf(a.type) - fixedOrder.indexOf(b.type));

    const margin = { top: 20, right: 30, bottom: 40, left: 180 };
    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;

    svg
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom);
      
    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);
    
    const xMax = d3.max(dataForChart, d => d.value);
    const x = d3.scaleLinear().domain([0, xMax > 0 ? xMax : 1]).range([0, width]);
    const y = d3.scaleBand().domain(dataForChart.map(d => d.type)).range([0, height]).padding(0.2);
    const countMax = d3.max(dataForChart, d => d.count) || 1;
    const opacityScale = d3.scaleLinear().domain([0, Math.max(countMax, 10)]).range([0.4, 1.0]);

    g.append('g').call(d3.axisLeft(y));
    g.append('g').attr('transform', `translate(0,${height})`).call(d3.axisBottom(x).ticks(5))
      .append("text")
        .attr("y", 35).attr("x", width / 2).attr("text-anchor", "middle").attr("fill", "black")
        .text("Average Days Before Death");

    const refLines = [30, 90, 180, 365];
    const refLinesGroup = g.append('g').attr('class', 'ref-lines');
    refLinesGroup.selectAll('line').data(refLines.filter(d => d <= xMax || xMax === 0)).enter()
      .append('line')
      .attr('x1', d => x(d)).attr('x2', d => x(d))
      .attr('y1', 0).attr('y2', height)
      .attr('stroke', '#999').attr('stroke-width', 1).attr('stroke-dasharray', '3,3');
    refLinesGroup.selectAll('text').data(refLines.filter(d => d <= xMax || xMax === 0)).enter()
      .append('text')
      .attr('x', d => x(d)).attr('y', -5)
      .attr('text-anchor', 'middle').attr('font-size', '10px').attr('fill', '#666')
      .text(d => `${d}d`);

    const lollipopGroup = g.selectAll('.lollipop').data(dataForChart).enter().append('g').attr('class', 'lollipop');
    
    lollipopGroup.append('line')
      .attr('x1', 0).attr('x2', d => x(d.value))
      .attr('y1', d => y(d.type) + y.bandwidth() / 2)
      .attr('y2', d => y(d.type) + y.bandwidth() / 2)
      .attr('stroke', '#ddd').attr('stroke-width', 1);
    
    const circleRadius = Math.min(y.bandwidth() * 0.3, 6);
    lollipopGroup.append('circle')
      .attr('cx', d => x(d.value))
      .attr('cy', d => y(d.type) + y.bandwidth() / 2)
      .attr('r', circleRadius)
      .attr('fill', '#3498db')
      .attr('fill-opacity', d => opacityScale(d.count))
      .append('title')
        .text(d => {
          let tooltip = `Avg: ${d.value.toFixed(1)} days (n=${d.count})`;
          if (d.stateAvg !== null) {
            tooltip += `\nState Avg: ${d.stateAvg.toFixed(1)} days`;
          }
          return tooltip;
        });

    if (countyData) {
      const benchmarkGroup = g.append('g').attr('class', 'benchmark-lines');
      benchmarkGroup.selectAll('line').data(dataForChart.filter(d => d.stateAvg !== null)).enter()
        .append('line')
        .attr('x1', d => x(d.stateAvg)).attr('x2', d => x(d.stateAvg))
        .attr('y1', d => y(d.type)).attr('y2', d => y(d.type) + y.bandwidth())
        .attr('stroke', 'black').attr('stroke-width', 2);
    }

  }, [countyData, indianaAverages, touchpointTypes, selectedYear, dimensions.width, dimensions.height]);

  return (
    <div ref={containerRef} className="chart-container">
      {!countyData && !indianaAverages && <div className="placeholder">Select a county to see recency data.</div>}
      <svg ref={svgRef} />
    </div>
  );
};

export default RecencyChart;